import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { VehiclePageContext } from "../../App";
import Swal from "sweetalert2";

const AllVehicleLayer = () => {
  const [records, setRecords] = useState([]);
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [update, setUpdate] = useState(0);
  const { vehiclePage, setVehiclePage } = useContext(VehiclePageContext);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const debounceFunction = setTimeout(() => {
      if (search.trim() !== "") {
        setQuery(search);
        setVehiclePage(0); // Reset to first page when searching
      } else {
        setQuery("");
      }
    }, 500);

    return () => clearTimeout(debounceFunction);
  }, [search]);

  useEffect(() => {
    const fetchAllVehicles = async () => {
      try {
        setLoading(true);
        const searchParams = new URLSearchParams({
          page: vehiclePage,
          limit: limit,
          search: query,
        });

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/vehicles?${searchParams}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const json = await response.json();

        if (response.ok) {
          setRecords(json.data.vehicles || []);
          setTotal(json.data.total);
          setHasNext(json.data.hasNext);
          setHasPrev(json.data.hasPrev);
        } else {
          setRecords([]);
        }
      } catch (error) {
        console.error("Error fetching vehicles:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllVehicles();
  }, [update, limit, vehiclePage, query, token]);

  const handleDelete = (vehicleId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
      customClass: {
        confirmButton: "btn btn-success btn-sm ms-2",
        cancelButton: "btn btn-sm",
        popup: "my-custom-modal",
        title: "my-custom-title",
        icon: "my-custom-icon",
        content: "my-custom-text",
      },
      buttonsStyling: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}api/admin/vehicles/${vehicleId}`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.ok) {
            setUpdate(update + 1);
            Swal.fire({
              title: "Deleted!",
              text: "Vehicle has been deleted.",
              icon: "success",
            });
          }
        } catch (error) {
          console.error("Error deleting vehicle:", error);
          Swal.fire({
            title: "Error",
            text: "Failed to delete vehicle",
            icon: "error",
          });
        }
      }
    });
  };

  const handleEdit = (id) => {
    navigate("/edit-vehicle/" + id);
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex flex-wrap align-items-center gap-3">
          <div className="d-flex align-items-center gap-2">
            <span>Show</span>
            <select
              className="form-select form-select-sm w-auto"
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value));
                setVehiclePage(0);
              }}
            >
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          <div className="icon-field">
            <input
              type="text"
              className="form-control form-control-sm w-auto"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <span className="icon">
              <Icon icon="ion:search-outline" />
            </span>
          </div>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-3">
          <Link to="/add-vehicle" className="btn btn-sm btn-primary-600">
            <i className="ri-add-line" /> Add Vehicle
          </Link>
        </div>
      </div>

      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th scope="col">ID</th>
                <th scope="col">Vehicle</th>
                <th scope="col">License Plate</th>
                <th scope="col">Fuel Type</th>
                <th scope="col">Transmission</th>
                <th scope="col">Owner</th>
                <th scope="col">Date Added</th>
                <th scope="col" className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    No vehicles found
                  </td>
                </tr>
              ) : (
                records.map((vehicle) => (
                  <tr key={vehicle.id}>
                    <td>{vehicle.id}</td>
                    <td>
                      <div className="d-flex align-items-center">
                        <img
                          src={vehicle.vehicleImageUrl || "assets/images/user-list/user-list1.png"}
                          alt="Vehicle"
                          className="w-40-px h-40-px rounded-circle flex-shrink-0 me-12 overflow-hidden object-fit-cover"
                        />
                        <span className="text-md mb-0 fw-normal text-secondary-light">
                          {vehicle.brand} {vehicle.model} ({vehicle.year})
                        </span>
                      </div>
                    </td>
                    <td>{vehicle.licensePlate}</td>
                    <td className="text-capitalize">{vehicle.fuelType}</td>
                    <td className="text-capitalize">{vehicle.transmissionType}</td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {vehicle.owner?.fullName || "-"}
                      </span>
                    </td>
                    <td>{vehicle.createdAt ? format(new Date(vehicle.createdAt), "dd MMM, yyyy") : "-"}</td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-10 justify-content-center">
                        <button
                          onClick={() => handleEdit(vehicle.id)}
                          className="bg-success-focus bg-hover-success-200 text-success-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          title="Edit"
                        >
                          <Icon icon="lucide:edit" className="menu-icon" />
                        </button>
                        <button
                          onClick={() => handleDelete(vehicle.id)}
                          className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          title="Delete"
                        >
                          <Icon icon="fluent:delete-24-regular" className="menu-icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <span>
            Showing {records.length} of {total} entries
          </span>
          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            <li className="page-item">
              <button
                className={`page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md ${!hasPrev ? "opacity-50 cursor-not-allowed" : ""}`}
                onClick={() => hasPrev && setVehiclePage(vehiclePage - 1)}
                disabled={!hasPrev}
              >
                <Icon icon="ep:d-arrow-left" />
              </button>
            </li>

            <li className="page-item">
              <span className="page-link bg-primary-600 text-white fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md">
                {vehiclePage + 1}
              </span>
            </li>

            <li className="page-item">
              <button
                className={`page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md ${!hasNext ? "opacity-50 cursor-not-allowed" : ""}`}
                onClick={() => hasNext && setVehiclePage(vehiclePage + 1)}
                disabled={!hasNext}
              >
                <Icon icon="ep:d-arrow-right" />
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AllVehicleLayer;
